import { Typography } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import { ParentSize } from '@visx/responsive';
import {
	AnimatedAxis,
	AnimatedBarSeries,
	AnimatedGrid,
	AnimatedLineSeries,
	Tooltip,
	XYChart,
	XYChartTheme
} from '@visx/xychart';
import React, { useContext } from 'react';
import { IAnalyticsContext, IWidgetItem, WidgetType } from 'types/analytics';
import { ITheme, ThemeMode } from 'types/theme';
import AnalyticsContext from '../analytics/AnalyticsContext';
import { XY_CHART_DARK_THEME, XY_CHART_LIGHT_THEME } from '../analytics/theme';
import {
	BAR_WIDGET_HEIGHT,
	countAccessor,
	getDateAccessor,
	tickLabelFormat,
	Y_AXIS_MAX_TICKS
} from '../analytics/utils';
import WidgetXTickComponent from './WidgetXTickComponent';
import WidgetYTickComponent from './WidgetYTickComponent';

interface IWidgetXYChartProps {
	items: IWidgetItem[];
	widgetType: WidgetType;
	theme?: Partial<XYChartTheme>;
}

const WidgetXYChart: React.FC<IWidgetXYChartProps> = ({
	items: analytics,
	widgetType,
	theme
}) => {
	const {
		palette: { mode, text }
	} = useTheme<ITheme>();
	const { xDomain, timePeriod } =
		useContext<IAnalyticsContext>(AnalyticsContext);
	const xAccessor = getDateAccessor(timePeriod);
	const yAccessor = countAccessor;

	const Widget =
		widgetType === WidgetType.Bar ? AnimatedBarSeries : AnimatedLineSeries;

	const tickLabelProps = React.useCallback(
		() => ({
			fill: text.primary
		}),
		[text.primary]
	);

	const selectedTheme =
		mode === ThemeMode.dark ? XY_CHART_DARK_THEME : XY_CHART_LIGHT_THEME;

	const yAnalytics = React.useMemo(
		() => analytics.map(yAccessor),
		[analytics, yAccessor]
	);

	const allXDomain = React.useMemo(
		() =>
			xDomain
				.map((date) =>
					xAccessor({
						date
					})
				)
				.sort(),
		[xAccessor, xDomain]
	);

	const yScaleMax = Math.max(...yAnalytics) * 1.1;
	const numTicks =
		yScaleMax > Y_AXIS_MAX_TICKS
			? Y_AXIS_MAX_TICKS
			: Math.round(yScaleMax % 10);

	const tickYComponent = React.useCallback(
		(props: any) => <WidgetYTickComponent value={props.formattedValue} />,
		[]
	);

	const tickXComponent = React.useCallback(
		(props: any) => <WidgetXTickComponent value={props.formattedValue} />,
		[]
	);

	return (
		<Box display="flex" height={BAR_WIDGET_HEIGHT}>
			<ParentSize debounceTime={10}>
				{(parent) => (
					<XYChart
						theme={{
							...selectedTheme,
							...theme
						}}
						xScale={{
							type: 'band',
							domain: allXDomain || [],
							round: true,
							nice: true,
							padding: 0.8
						}}
						yScale={{
							type: 'linear',
							domain: [0, yScaleMax],
							round: true,
							nice: true
						}}
						height={parent.height || 100}
						width={parent.width || 100}
					>
						<AnimatedAxis
							data-testid="x-axis"
							orientation="bottom"
							tickLabelProps={tickLabelProps}
							tickValues={allXDomain}
							tickComponent={tickXComponent}
						/>
						<AnimatedAxis
							orientation="left"
							tickLabelProps={tickLabelProps}
							numTicks={numTicks}
							tickComponent={tickYComponent}
						/>
						<AnimatedGrid columns rows />
						<Widget
							wordSpacing={1}
							dataKey="chart"
							data={analytics}
							xAccessor={xAccessor}
							yAccessor={yAccessor}
						/>
						<Tooltip
							snapTooltipToDatumX
							snapTooltipToDatumY
							showVerticalCrosshair
							showDatumGlyph
							showHorizontalCrosshair
							showSeriesGlyphs
							renderTooltip={({ tooltipData }) => {
								if (!tooltipData?.nearestDatum?.datum) return intl.tr('app.na');

								const { datum } = tooltipData.nearestDatum;
								const x = xAccessor(datum as IWidgetItem);
								const y = yAccessor(datum as IWidgetItem);

								return (
									<div>
										<Typography variant="body1" fontWeight="bold">
											{intl.formatNumber(y)}
										</Typography>
										<Typography variant="body1">
											{`${tickLabelFormat(timePeriod)(x)}`}
										</Typography>
									</div>
								);
							}}
						/>
					</XYChart>
				)}
			</ParentSize>
		</Box>
	);
};

WidgetXYChart.defaultProps = {
	theme: {}
};

export default WidgetXYChart;
